import { Box, Typography } from "@mui/material";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { hubAction } from "redux/slice/hub";
import ScrollToBottom from "utils/ScrollToBottom";
import WinWireMessage from "./WinWireMessage";

const WinWire = ({ messages, user, isMobileDisplay, scrollToBottom }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const messageRefs = useRef({});
  const { loading, companyLogo } = useSelector((state) => state.hub);
  const bottomRef = useRef(null);
  const containerRef = useRef(null);
  const [highlightedMessage, setHighlightedMessage] = useState(null);
  const [messageIdFromQuery, setMessageIdFromQuery] = useState(null);
  const queryDateRef = useRef(
    moment.tz(user.timezone).startOf("day").subtract(30, "days").utc().format("YYYY-MM-DD HH:mm:ss")
  );
  const shouldScrollRef = useRef(true);
  const shouldRecalculateDateRef = useRef(false);

  const calculateDateRef = () => {
    if (messages && messages.length > 0) {
      const currentDateRef = moment.tz(queryDateRef.current, user.timezone).startOf("day").utc();
      const oldestMessageDate = moment
        .utc(
          messages.reduce(
            (oldest, message) =>
              !oldest || moment.utc(message.createdAt).isBefore(moment.utc(oldest))
                ? message.createdAt
                : oldest,
            null
          )
        )
        .startOf("day");

      queryDateRef.current = moment
        .min(currentDateRef, oldestMessageDate)
        .format("YYYY-MM-DD HH:mm:ss");
    }
  };

  const loadOlderMessages = () => {
    if (!loading) {
      const fromDate = moment(queryDateRef.current).subtract(1, "ms").format("YYYY-MM-DD HH:mm:ss");
      dispatch(
        hubAction.getWinWireMessagesSaga({
          daysBack: 30,
          fromDate,
        })
      );
      queryDateRef.current = moment(queryDateRef.current)
        .subtract(30, "days")
        .format("YYYY-MM-DD HH:mm:ss");
    }
  };

  const loadToMessage = (messageId) => {
    if (!loading) {
      const fromDate = moment(queryDateRef.current).subtract(1, "ms").format("YYYY-MM-DD HH:mm:ss");
      dispatch(
        hubAction.getWinWireMessagesSaga({
          fromDate,
          messageId,
        })
      );

      // trigger recalculation of date reference once messages are loaded
      shouldRecalculateDateRef.current = true;
    }
  };

  const scrollToMessage = (messageId) => {
    const messageRef = messageRefs.current[+messageId];
    if (messageRef) {
      messageRef.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
      if (messageId !== highlightedMessage) {
        setHighlightedMessage(+messageId);
        setTimeout(() => setHighlightedMessage(null), 3000);
      }
    }
  };

  const handleScroll = () => {
    if (shouldScrollRef.current) {
      if (messageIdFromQuery && messageRefs.current[+messageIdFromQuery]) {
        scrollToMessage(messageIdFromQuery);
      } else {
        bottomRef.current?.scrollIntoView({ behavior: "auto", block: "nearest" });
      }
    }
    shouldScrollRef.current = false;
  };

  useEffect(() => {
    if (!messages || messages.length === 0) return;
    handleScroll();

    if (shouldRecalculateDateRef.current) {
      calculateDateRef();
      shouldRecalculateDateRef.current = false;
    }
  }, [messages]);

  // load messages if query changes, scroll to message if it exists
  useEffect(() => {
    shouldScrollRef.current = true;
    if (messageRefs.current[+messageIdFromQuery]) {
      handleScroll();
    } else if (messageIdFromQuery) {
      loadToMessage(messageIdFromQuery);
    }
  }, [messageIdFromQuery]);

  // handle hash changes
  useEffect(() => {
    const { hash } = location;
    const messageId = new URLSearchParams(hash.split("?")[1]).get("messageId");
    if (messageIdFromQuery !== messageId) {
      setMessageIdFromQuery(messageId);
    }
  }, [location]);

  useEffect(() => {
    // remove notifications
    dispatch(hubAction.removeNotifications({ key: "winWire" }));

    // handle scrolling to the top of the container
    const handleScrollToTop = () => {
      if (!containerRef.current) return;

      if (containerRef.current.scrollTop === 0) {
        loadOlderMessages();
      }
    };

    const container = containerRef.current;
    if (container) {
      container.addEventListener("scroll", handleScrollToTop);
    }

    return () => {
      if (container) {
        container.removeEventListener("scroll", handleScrollToTop);
      }
    };
  }, []);

  let currentDay = null;

  return (
    <Box ref={containerRef} className="hub-content-window">
      {messages && messages.length > 0 ? (
        messages.map((message) => {
          const messageDay = moment.tz(message.createdAt, user.timezone).format("YYYY-MM-DD");
          const shouldShowDayHeader = currentDay !== messageDay;
          currentDay = messageDay;

          return (
            <Box key={message.id}>
              {shouldShowDayHeader && (
                <Box sx={{ mb: 1, mt: 2 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      width: "100%",
                    }}
                  >
                    <Box className="divider" />
                    <Typography
                      variant="h6"
                      sx={{
                        textAlign: "center",
                        fontWeight: 700,
                        fontSize: "0.9rem",
                        color: "#666",
                        whiteSpace: "nowrap",
                        mx: "0.5rem",
                      }}
                    >
                      {moment.tz(message.createdAt, user.timezone).format("dddd, MMMM Do")}
                    </Typography>
                    <Box className="divider" />
                  </Box>
                </Box>
              )}
              <WinWireMessage
                ref={(msg) => {
                  messageRefs.current[message.id] = msg;
                }}
                message={message}
                key={message.id}
                user={user}
                companyLogo={companyLogo}
                highlightedMessage={highlightedMessage}
              />
            </Box>
          );
        })
      ) : (
        <Typography sx={{ textAlign: "left", fontSize: "1rem", ml: "2rem", mt: "1rem" }}>
          No messages to display
        </Typography>
      )}
      <Box ref={bottomRef} />
      <ScrollToBottom
        containerRef={containerRef}
        onClick={() => scrollToBottom(bottomRef)}
        isMobileDisplay={isMobileDisplay}
      />
    </Box>
  );
};

export default WinWire;
